import { useState } from "react";
import Icon from "../../components/Icon";
import Drawer from "../../components/Drawer";
import { generateTasks, sendReminders } from "../../api/opsService";
import {
  opsGeneratePayments,
  opsSweepOverduePayments,
  opsSendPaymentReminders,
  opsIssueTaskInvoices,
} from "../../api/paymentsService";
import { useOpsRun } from "../../hooks/useOpsRun";
import { showApiError, showToast } from "../../utils/toast";

export default function OpsPage() {
  const today = new Date().toISOString().slice(0, 10);
  // From/to bounds for both ops — the backend splits the inclusive range into
  // one queued job per day and reports per-day plus aggregated totals as it goes.
  const [genFrom, setGenFrom] = useState(today);
  const [genTo, setGenTo] = useState(today);
  const [reminderFrom, setReminderFrom] = useState(today);
  const [reminderTo, setReminderTo] = useState(today);
  // The four ranged ops are queued server-side — the hook starts a run and
  // polls it. Only the single-day sweep and invoice pass answer inline.
  const gen = useOpsRun("ops:tasks.generate");
  const rem = useOpsRun("ops:tasks.reminders");

  // Payments ops
  const [payGenFrom, setPayGenFrom] = useState(today);
  const [payGenTo, setPayGenTo] = useState(today);
  const [payRemFrom, setPayRemFrom] = useState(today);
  const [payRemTo, setPayRemTo] = useState(today);
  const [paySweepDate, setPaySweepDate] = useState(today);
  const payGen = useOpsRun("ops:payments.generate");
  const payRem = useOpsRun("ops:payments.reminders");
  const [paySweepResult, setPaySweepResult] = useState(null);
  const [paySweepBusy, setPaySweepBusy] = useState(false);

  // Auto-invoiced tasks — single date, see the card comment.
  const [invIssueDate, setInvIssueDate] = useState(today);
  const [invIssueResult, setInvIssueResult] = useState(null);
  const [invIssueBusy, setInvIssueBusy] = useState(false);

  const runPaySweep = async () => {
    setPaySweepBusy(true);
    try {
      const res = await opsSweepOverduePayments({ date: paySweepDate });
      setPaySweepResult(res);
      showToast("success", res.message || "Overdue sweep complete");
    } catch (err) {
      showApiError(err);
    } finally {
      setPaySweepBusy(false);
    }
  };

  const runIssueInvoices = async () => {
    setInvIssueBusy(true);
    try {
      const res = await opsIssueTaskInvoices({ date: invIssueDate });
      setInvIssueResult(res);
      showToast("success", res.message || "Invoices issued");
    } catch (err) {
      showApiError(err);
    } finally {
      setInvIssueBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Ops Runner</h1>
          <div className="page-sub">Manually trigger task generation &amp; reminder dispatch</div>
        </div>
      </div>

      <div
        className="card"
        style={{
          background: "var(--info-100)",
          borderColor: "#bfdbfe",
          marginBottom: 16,
        }}
      >
        <div className="card-body row gap-sm" style={{ alignItems: "flex-start" }}>
          <Icon name="alert-circle" size={18} style={{ color: "var(--info)", marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div className="t-primary fw-600" style={{ marginBottom: 4 }}>How generation works</div>
            <div className="text-sm t-muted">
              Each recurrence pattern only materialises tasks <b>on its reminder day</b>:
              monthly fires on <code>monthly_reminder_day</code>, yearly on its reminder month+day,
              half-yearly/quarterly on each configured reminder date, weekly on the configured weekday.
              Daily and one-off always generate. Picking a <b>From / To</b> range simulates a cron sweep
              across those days — useful for back-filling missed runs after downtime.
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <div className="row gap-sm">
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--primary-50)", color: "var(--primary-700)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="zap" size={16} />
              </div>
              <div>
                <div className="card-title">Generate task instances</div>
                <div className="card-sub">Materialise recurring task masters into employee task instances</div>
              </div>
            </div>
          </div>
          <div className="card-body col gap-md">
            <DateRange
              from={genFrom}
              to={genTo}
              onFrom={setGenFrom}
              onTo={setGenTo}
            />
            {gen.progress && <RunProgressCard p={gen.progress} onCancel={gen.cancel} />}
            {gen.result && <GenerateResultCard result={gen.result} />}
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button
                className="btn btn-primary"
                onClick={() => gen.start(generateTasks, { from: genFrom, to: genTo })}
                disabled={gen.busy}
              >
                <Icon name="zap" size={13} /> {gen.busy ? runLabel(gen.progress) : "Run generator"}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="row gap-sm">
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--warning-100)", color: "var(--warning)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="bell" size={16} />
              </div>
              <div>
                <div className="card-title">Send reminders</div>
                <div className="card-sub">Dispatch notifications for upcoming and overdue tasks</div>
              </div>
            </div>
          </div>
          <div className="card-body col gap-md">
            <DateRange
              from={reminderFrom}
              to={reminderTo}
              onFrom={setReminderFrom}
              onTo={setReminderTo}
            />
            {rem.progress && <RunProgressCard p={rem.progress} onCancel={rem.cancel} />}
            {rem.result && <ReminderResultCard result={rem.result} />}
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button
                className="btn btn-primary"
                onClick={() => rem.start(sendReminders, { from: reminderFrom, to: reminderTo })}
                disabled={rem.busy}
              >
                <Icon name="send" size={13} /> {rem.busy ? runLabel(rem.progress) : "Send reminders"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <h2 className="page-title" style={{ fontSize: 18, marginTop: 24, marginBottom: 8 }}>Payments</h2>
      <div className="text-sm t-muted" style={{ marginBottom: 12 }}>
        Daily cron runs at 00:10 / 00:15 / 00:40 / 10:05 IST. Manual triggers below are for back-fills
        and one-off catch-ups.
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <div className="row gap-sm">
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--success-100)", color: "var(--success)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="dollar-sign" size={16} />
              </div>
              <div>
                <div className="card-title">Generate bills</div>
                <div className="card-sub">Materialise payment instances per attached template</div>
              </div>
            </div>
          </div>
          <div className="card-body col gap-md">
            <DateRange from={payGenFrom} to={payGenTo} onFrom={setPayGenFrom} onTo={setPayGenTo} />
            {payGen.progress && <RunProgressCard p={payGen.progress} onCancel={payGen.cancel} />}
            {payGen.result && <PayGenerateResult result={payGen.result} />}
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button
                className="btn btn-primary"
                onClick={() => payGen.start(opsGeneratePayments, { from: payGenFrom, to: payGenTo })}
                disabled={payGen.busy}
              >
                <Icon name="dollar-sign" size={13} /> {payGen.busy ? runLabel(payGen.progress) : "Run generator"}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="row gap-sm">
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fee2e2", color: "var(--danger)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="alert-triangle" size={16} />
              </div>
              <div>
                <div className="card-title">Mark overdue</div>
                <div className="card-sub">Flip past-due pending/partial bills to overdue + fire N3</div>
              </div>
            </div>
          </div>
          <div className="card-body col gap-md">
            <div className="field" style={{ maxWidth: 200 }}>
              <label className="field-label">As of date</label>
              <input
                type="date"
                className="input"
                value={paySweepDate}
                onChange={(e) => setPaySweepDate(e.target.value)}
              />
            </div>
            {paySweepResult && <PaySweepResult result={paySweepResult} />}
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={runPaySweep} disabled={paySweepBusy}>
                <Icon name="alert-triangle" size={13} /> {paySweepBusy ? "Running…" : "Mark overdue"}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="row gap-sm">
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--warning-100)", color: "var(--warning)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="bell" size={16} />
              </div>
              <div>
                <div className="card-title">Send payment reminders (N2)</div>
                <div className="card-sub">Pre-due pings to admins + customers, per template's lead time</div>
              </div>
            </div>
          </div>
          <div className="card-body col gap-md">
            <DateRange from={payRemFrom} to={payRemTo} onFrom={setPayRemFrom} onTo={setPayRemTo} />
            {payRem.progress && <RunProgressCard p={payRem.progress} onCancel={payRem.cancel} />}
            {payRem.result && <PayReminderResult result={payRem.result} />}
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button
                className="btn btn-primary"
                onClick={() => payRem.start(opsSendPaymentReminders, { from: payRemFrom, to: payRemTo })}
                disabled={payRem.busy}
              >
                <Icon name="send" size={13} /> {payRem.busy ? runLabel(payRem.progress) : "Send reminders"}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="row gap-sm">
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--primary-100)", color: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="file-text" size={16} />
              </div>
              <div>
                <div className="card-title">Issue task invoices</div>
                <div className="card-sub">Raise the bill + invoice for verified auto-invoiced tasks that are due</div>
              </div>
            </div>
          </div>
          <div className="card-body col gap-md">
            {/* Single date, not a range: these bills have no payment master, so
                the DB can't dedupe them — the only guard is the per-task
                "already issued" stamp, which a range would just re-scan. */}
            <div className="field" style={{ maxWidth: 200 }}>
              <label className="field-label">As of date</label>
              <input
                type="date"
                className="input"
                value={invIssueDate}
                onChange={(e) => setInvIssueDate(e.target.value)}
              />
            </div>
            {invIssueResult && <IssueInvoicesResult result={invIssueResult} />}
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={runIssueInvoices} disabled={invIssueBusy}>
                <Icon name="file-text" size={13} /> {invIssueBusy ? "Running…" : "Issue invoices"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function IssueInvoicesResult({ result }) {
  const d = result.data || {};
  const issued = d.issued ?? 0;
  const skipped = d.skipped ?? 0;
  const errors = Array.isArray(d.errors) ? d.errors : [];
  const ok = issued > 0;
  const c = ok
    ? { bg: "var(--success-100)", border: "#a7f3d0", text: "var(--success)" }
    : { bg: "var(--surface-2)", border: "var(--border)", text: "var(--ink-700)" };
  return (
    <div className="card" style={{ background: c.bg, borderColor: c.border }}>
      <div className="card-body col gap-sm">
        <div className="row gap-sm">
          <Icon name={ok ? "check-circle" : "alert-circle"} size={20} style={{ color: c.text }} />
          <div className="t-primary fw-600" style={{ color: c.text }}>
            {ok ? `Issued ${issued} invoice${issued === 1 ? "" : "s"}` : "Nothing due"}
          </div>
        </div>
        <div className="row gap-md">
          <OpStat label="Issued" value={issued} />
          <OpStat label="Skipped" value={skipped} />
          {d.date && <OpStat label="As of" value={d.date} />}
        </div>
        {errors.length > 0 && (
          <div className="col gap-sm">
            {errors.map((e, i) => (
              <div key={i} className="text-xs" style={{ color: "var(--danger)" }}>{e}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PayGenerateResult({ result }) {
  const d = result.data || {};
  const generated = d.generated ?? 0;
  const skipped = d.skipped ?? 0;
  const errors = Array.isArray(d.errors) ? d.errors : [];
  const ok = generated > 0;
  const c = ok
    ? { bg: "var(--success-100)", border: "#a7f3d0", text: "var(--success)" }
    : { bg: "var(--surface-2)", border: "var(--border)", text: "var(--ink-700)" };
  const window = d.from && d.to ? (d.from === d.to ? d.from : `${d.from} → ${d.to}`) : null;
  return (
    <div className="card" style={{ background: c.bg, borderColor: c.border }}>
      <div className="card-body col gap-sm">
        <div className="row gap-sm">
          <Icon name={ok ? "check-circle" : "alert-circle"} size={20} style={{ color: c.text }} />
          <div className="t-primary fw-600" style={{ color: c.text }}>
            {ok ? `Generated ${generated} bill${generated === 1 ? "" : "s"}` : "Nothing to generate"}
          </div>
        </div>
        {window && <div className="text-xs t-muted">Range: <b>{window}</b> · {d.days ?? 1} day{(d.days ?? 1) === 1 ? "" : "s"}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <OpStat label="Generated" value={generated} />
          <OpStat label="Skipped" value={skipped} />
          <OpStat label="Errors" value={errors.length} />
        </div>
        {errors.length > 0 && (
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, padding: 10, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, maxHeight: 160, overflow: "auto" }}>
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

function PaySweepResult({ result }) {
  const d = result.data || {};
  return (
    <div className="card" style={{ background: "var(--surface-2)", borderColor: "var(--border)" }}>
      <div className="card-body row gap-md" style={{ flexWrap: "wrap" }}>
        <OpStat label="Flipped to overdue" value={d.flipped ?? 0} />
        <OpStat label="Notifications fired" value={d.notified ?? 0} />
        <OpStat label="As of" value={d.date || "—"} />
      </div>
    </div>
  );
}

function PayReminderResult({ result }) {
  const d = result.data || {};
  const window = d.from && d.to ? (d.from === d.to ? d.from : `${d.from} → ${d.to}`) : null;
  return (
    <div className="card" style={{ background: "var(--info-100)", borderColor: "#bfdbfe" }}>
      <div className="card-body col gap-sm">
        <div className="row gap-sm">
          <Icon name="check-circle" size={20} style={{ color: "var(--info)" }} />
          <div className="t-primary fw-600">{result.message || "Reminders dispatched"}</div>
        </div>
        {window && <div className="text-xs t-muted">Range: <b>{window}</b> · {d.days ?? 1} day{(d.days ?? 1) === 1 ? "" : "s"}</div>}
        <OpStat label="Notified" value={d.notified ?? 0} />
      </div>
    </div>
  );
}

function OpStat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-900)", letterSpacing: -0.5, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function DateRange({ from, to, onFrom, onTo }) {
  return (
    <div className="row gap-md" style={{ flexWrap: "wrap" }}>
      <div className="field" style={{ flex: 1, minWidth: 160 }}>
        <label className="field-label">From</label>
        <input
          type="date"
          className="input"
          value={from}
          max={to || undefined}
          onChange={(e) => onFrom(e.target.value)}
        />
      </div>
      <div className="field" style={{ flex: 1, minWidth: 160 }}>
        <label className="field-label">To</label>
        <input
          type="date"
          className="input"
          value={to}
          min={from || undefined}
          onChange={(e) => onTo(e.target.value)}
        />
      </div>
    </div>
  );
}

// Button label while a queued run is in flight. Falls back to a plain
// "Running…" until the first poll comes back with a day count.
function runLabel(p) {
  if (!p || !p.days) return "Running…";
  return `Running… ${p.days_done ?? 0}/${p.days} days`;
}

// Live progress for a queued ops run. Replaced by the result card once the
// run reaches a terminal state.
function RunProgressCard({ p, onCancel }) {
  const pct = Math.round((p.progress ?? 0) * 100);
  return (
    <div className="card" style={{ background: "var(--info-100)", borderColor: "#bfdbfe" }}>
      <div className="card-body col gap-sm">
        <div className="row gap-sm" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div className="row gap-sm">
            <Icon name="refresh-cw" size={18} style={{ color: "var(--info)" }} />
            <div className="t-primary fw-600">
              Running on the server — day {p.days_done ?? 0} of {p.days ?? 0}
            </div>
          </div>
          {onCancel && (
            <button className="btn btn-secondary btn-sm" onClick={onCancel}>
              <Icon name="x" size={12} /> Cancel
            </button>
          )}
        </div>
        <div style={{ height: 6, background: "var(--surface-2)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--info)", transition: "width .3s" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <Stat label="Generated" value={p.generated ?? p.sent ?? p.notified ?? 0} />
          <Stat label="Skipped" value={p.skipped ?? 0} />
          <Stat label="Errors" value={p.errors_total ?? 0} />
        </div>
        <div className="text-xs t-muted">
          Safe to leave this page — the run continues on the server and this card
          reattaches when you come back.
        </div>
      </div>
    </div>
  );
}

function GenerateResultCard({ result }) {
  // The day-by-day breakdown can run to hundreds of rows for a wide
  // back-fill range. Keep the card to a compact summary and tuck the full
  // table behind a "View" button + drawer.
  const [showDaily, setShowDaily] = useState(false);
  const data = result.data || {};
  const generated = data.generated ?? 0;
  const skipped = data.skipped ?? 0;
  const errors = Array.isArray(data.errors) ? data.errors : [];
  const days = data.days ?? 1;
  const daily = Array.isArray(data.daily) ? data.daily : [];
  const hasDailyBreakdown = daily.length > 1;
  const ok = generated > 0;
  const tone = ok ? "success" : "neutral";
  const colorMap = {
    success: { bg: "var(--success-100)", border: "#a7f3d0", text: "var(--success)" },
    neutral: { bg: "var(--surface-2)", border: "var(--border)", text: "var(--ink-700)" },
  };
  const c = colorMap[tone];
  const window = data.from && data.to
    ? (data.from === data.to ? data.from : `${data.from} → ${data.to}`)
    : null;

  return (
    <div className="card" style={{ background: c.bg, borderColor: c.border }}>
      <div className="card-body col gap-sm">
        <div className="row gap-sm">
          <Icon
            name={ok ? "check-circle" : "alert-circle"}
            size={20}
            style={{ color: c.text }}
          />
          <div className="t-primary fw-600" style={{ color: c.text }}>
            {ok ? `Generated ${generated} task instance${generated === 1 ? "" : "s"}` : "Nothing to generate"}
          </div>
        </div>
        {window && (
          <div className="text-xs t-muted">
            Range: <b>{window}</b> · {days} day{days === 1 ? "" : "s"}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <Stat label="Generated" value={generated} />
          <Stat label="Skipped (already exists)" value={skipped} />
          <Stat label="Errors" value={errors.length} />
        </div>
        {!ok && (
          <div className="text-xs t-muted">
            No task master had a reminder date matching the selected range.
            Try a range that includes one of your reminder days, or create a Daily / One-off
            task master to confirm the runner is working.
          </div>
        )}
        {hasDailyBreakdown && (
          <div
            className="row"
            style={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <span className="text-xs t-muted">
              Day-by-day breakdown across {daily.length} days
            </span>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowDaily(true)}>
              <Icon name="eye" size={12} /> View breakdown
            </button>
          </div>
        )}
        {errors.length > 0 && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              padding: 10,
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              maxHeight: 160,
              overflow: "auto",
            }}
          >
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}
      </div>

      <Drawer
        open={showDaily}
        onClose={() => setShowDaily(false)}
        title="Generation — daily breakdown"
        subtitle={window ? `Range: ${window} · ${days} day${days === 1 ? "" : "s"}` : null}
      >
        <DailyTable rows={daily} columns={["generated", "skipped", "errors"]} />
      </Drawer>
    </div>
  );
}

function ReminderResultCard({ result }) {
  const data = result.data || {};
  const window = data.from && data.to
    ? (data.from === data.to ? data.from : `${data.from} → ${data.to}`)
    : null;
  // Stats are everything except the meta keys.
  const stats = Object.entries(data).filter(
    ([k]) => !["from", "to", "days", "daily"].includes(k),
  );
  return (
    <div className="card" style={{ background: "var(--info-100)", borderColor: "#bfdbfe" }}>
      <div className="card-body col gap-sm">
        <div className="row gap-sm">
          <Icon name="check-circle" size={20} style={{ color: "var(--info)" }} />
          <div className="t-primary fw-600">{result.message || "Reminders dispatched"}</div>
        </div>
        {window && (
          <div className="text-xs t-muted">
            Range: <b>{window}</b> · {data.days ?? 1} day{(data.days ?? 1) === 1 ? "" : "s"}
          </div>
        )}
        {stats.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            {stats.map(([k, v]) => (
              <Stat
                key={k}
                label={k.replace(/_/g, " ")}
                value={typeof v === "number" || typeof v === "string" ? v : JSON.stringify(v)}
              />
            ))}
          </div>
        )}
        {Array.isArray(data.daily) && data.daily.length > 1 && (
          <DailyTable
            rows={data.daily}
            columns={Object.keys(data.daily[0] || {}).filter((k) => k !== "date")}
          />
        )}
      </div>
    </div>
  );
}

function DailyTable({ rows, columns }) {
  if (!Array.isArray(rows) || rows.length === 0 || columns.length === 0) return null;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      <table className="table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Date</th>
            {columns.map((c) => (
              <th key={c} className="t-num">{c.replace(/_/g, " ")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.date}>
              <td className="t-muted">{r.date}</td>
              {columns.map((c) => (
                <td key={c} className="t-num">{r[c] ?? 0}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-900)", letterSpacing: -0.5, marginTop: 2 }}>{value}</div>
    </div>
  );
}
